import React from "react";
import { useTranslation } from "i18n";
import cx from "classnames";
import { Formik, Form, Field } from "formik";
import { useToggle } from "usehooks-ts";
import { useIsMobile } from "../../../../../generics/dom-extensions";
import { ReactComponent as SearchIcon } from "../../../../../images/svg/icons/search.svg";
import { ReactComponent as CloseIcon } from "../../../../../images/svg/icons/cross-thin.svg";
import { ReactComponent as RemoveTextIcon } from "../../../../../images/svg/icons/inscribed-cross.svg";
import { focusStyle } from "../../constants";

interface Props {
  submitSearch: (searchTerm: string) => void;
  renderAutoCompleteResult?: (searchTerm: string) => React.ReactNode;
}

const NavSearch = ({ submitSearch, renderAutoCompleteResult }: Props) => {
  const isMobile = useIsMobile();
  const [mobileSearchIsVisible, toggleMobileSearch] = useToggle(false);
  const { t } = useTranslation();

  const searchButtonClass = cx(
    "h-10 w-10 flex items-center justify-center text-white",
    "hover:text-color-accent-one text-ellipsis",
    focusStyle
  );

  const formClass = cx(
    "flex items-center h-10 px-2.5 w-full md:w-auto mt-1 md:mt-0",
    "order-2 md:order-none bg-color-neutral-one",
    {
      hidden: !mobileSearchIsVisible && isMobile,
    }
  );

  return (
    <>
      {isMobile && (
        <div>
          <button
            type="submit"
            className={searchButtonClass}
            onClick={toggleMobileSearch}
          >
            {mobileSearchIsVisible ? <CloseIcon /> : <SearchIcon />}
          </button>
        </div>
      )}

      <Formik
        initialValues={{ search: "" }}
        onSubmit={(values) => {
          if (values.search) submitSearch(values.search);
        }}
      >
        {({ values, resetForm }) => (
          <Form className={formClass}>
            <div className="relative h-full w-full">
              <Field
                name="search"
                type="text"
                autoComplete="off"
                placeholder={t("common:nav.search.input.placeholder")}
                className={cx(
                  "md2:w-[240px] bg-grey-shark-5 h-full w-full text-ellipsis rounded px-2.5 py-[5px] pr-10 placeholder-white md:w-auto",
                  "focus:text-color-neutral-one outline-none focus:bg-white focus:placeholder-transparent"
                )}
              />
              {renderAutoCompleteResult?.(values.search)}
              {values.search && (
                <button
                  type="button"
                  onClick={() => resetForm()}
                  className={cx(
                    "text-color-neutral-two absolute right-1 top-0 h-full p-2",
                    focusStyle
                  )}
                >
                  <RemoveTextIcon height={16} />
                </button>
              )}
            </div>

            <button type="submit" className={searchButtonClass}>
              <SearchIcon />
            </button>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default NavSearch;
